import { useState } from 'react'
import { Filter, Search, X } from 'lucide-react'
import { SuporteFilters } from '../tipos/suporte'
import { InputDate } from '../../../componentesCompartilhados/form/InputDate'
import { useAnalistasSMN, useDesenvolvedores, usePrioridades, useStatusProjeto } from '../../../hooks/useDomainData'

interface SuporteFiltersProps {
  onSearch: (filters: SuporteFilters) => void
}

export function SuporteFiltersComponent({ onSearch }: SuporteFiltersProps) {
  const [filters, setFilters] = useState<SuporteFilters>({
    search: '',
    statusCodigo: '',
    prioridadeCodigo: '',
    dataInicio: '',
    dataFim: '',
    analistaSMNId: '',
    desenvolvedorId: '',
  })
  const { data: status = [] } = useStatusProjeto()
  const { data: prioridades = [] } = usePrioridades()
  const { data: analistasSMN = [] } = useAnalistasSMN()
  const { data: desenvolvedores = [] } = useDesenvolvedores()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(filters)
  }

  const handleClear = () => {
    const clearedFilters: SuporteFilters = {
      search: '',
      statusCodigo: '',
      prioridadeCodigo: '',
      dataInicio: '',
      dataFim: '',
      analistaSMNId: '',
      desenvolvedorId: '',
    }
    setFilters(clearedFilters)
    onSearch(clearedFilters)
  }

  const handleInputChange = (field: keyof SuporteFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <Filter className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Filtros de Suporte</h3>
            <p className="text-sm text-gray-600">Refine a listagem de acordo com a necessidade</p>
          </div>
        </div>
        <button
          onClick={handleClear}
          className="px-3 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium"
        >
          <X className="w-4 h-4 mr-2 inline" />
          Limpar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
            <div className="relative">
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleInputChange('search', e.target.value)}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Buscar por nome ou número do ticket..."
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.statusCodigo || ''}
              onChange={(e) => handleInputChange('statusCodigo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Todos</option>
              {status.map((item: any) => (
                <option key={item.codigo ?? item.Codigo} value={String(item.codigo ?? item.Codigo)}>
                  {item.descricao ?? item.Descricao}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prioridade</label>
            <select
              value={filters.prioridadeCodigo || ''}
              onChange={(e) => handleInputChange('prioridadeCodigo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Todas</option>
              {prioridades.map((item: any) => (
                <option key={item.codigo ?? item.Codigo} value={String(item.codigo ?? item.Codigo)}>
                  {item.descricao ?? item.Descricao}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data início</label>
            <InputDate
              value={filters.dataInicio || ''}
              onChange={(valor) => handleInputChange('dataInicio', valor)}
              className="py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              showError={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data fim</label>
            <InputDate
              value={filters.dataFim || ''}
              onChange={(valor) => handleInputChange('dataFim', valor)}
              className="py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              showError={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Analista SMN</label>
            <select
              value={filters.analistaSMNId || ''}
              onChange={(e) => handleInputChange('analistaSMNId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Todos</option>
              {analistasSMN.map((pessoa: any) => (
                <option key={pessoa.idPessoa ?? pessoa.IdPessoa} value={String(pessoa.idPessoa ?? pessoa.IdPessoa)}>
                  {pessoa.nome ?? pessoa.Nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Desenvolvedor</label>
            <select
              value={filters.desenvolvedorId || ''}
              onChange={(e) => handleInputChange('desenvolvedorId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Todos</option>
              {desenvolvedores.map((pessoa: any) => (
                <option key={pessoa.idPessoa ?? pessoa.IdPessoa} value={String(pessoa.idPessoa ?? pessoa.IdPessoa)}>
                  {pessoa.nome ?? pessoa.Nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:col-span-3 flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium"
          >
            <Search className="w-4 h-4 mr-2 inline" />
            Buscar Suportes
          </button>
        </div>
      </form>
    </div>
  )
}
